import { MemberFormStatus } from "clinical-forms-experience-client";
import { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { getAllMemberForms } from "./apiUtils.assessment";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import { LoadingHelper } from "../../components/loader/LoadingHelper";

export const ASSESSMENT_OVERDUE_DAYS_THRESHOLD = 90;

export const isAssessmentOverdue = (
  lastCompletionDate: string | number | Date,
  thresholdDays: number = ASSESSMENT_OVERDUE_DAYS_THRESHOLD
): boolean => {
  const daysDiff =
    (new Date().getTime() - new Date(lastCompletionDate).getTime()) /
    (1000 * 60 * 60 * 24);
  return daysDiff >= thresholdDays;
};

// Next scheduled occurrence of a module: lastCompletionDate + the overdue
// threshold. Used both for the "Due MM/DD/YYYY" label on an overdue
// (Current) module and to derive a "Next up" date for a module that was
// completed but isn't overdue yet.
export const getModuleNextDueDate = (
  lastCompletionDate: string | number | Date,
  thresholdDays: number = ASSESSMENT_OVERDUE_DAYS_THRESHOLD
): Date => {
  const due = new Date(lastCompletionDate);
  due.setDate(due.getDate() + thresholdDays);
  return due;
};

export const getAssessmentOverdueStatus = (
  lastCompletionDate: string | number | Date
) => {
  const daysSince = Math.floor(
    (new Date().getTime() - new Date(lastCompletionDate).getTime()) /
    (1000 * 60 * 60 * 24)
  );
  return {
    isOverdue: isAssessmentOverdue(lastCompletionDate),
    daysSince,
  };
};

// Clinical score direction: whether higher or lower values indicate improvement
export type ScoreDirection = "higher-is-better" | "lower-is-better";

// Assessment name (matches clinical_forms.name) -> clinical direction
// Only include types with a confident basis (explicit scoring text or
// well-known clinical/industry convention) - unlisted types render without
// a trend arrow rather than guessing at a possibly clinically wrong signal
export const ASSESSMENT_SCORE_DIRECTION: Record<string, ScoreDirection> = {
  "COPD Assessment Test (CAT)": "lower-is-better",
  "mMRC Dyspnea Scale": "lower-is-better",
  Psychosocial: "lower-is-better",
  "Patient Activation Measure (PAM)": "higher-is-better",
  NPS: "higher-is-better",
};

export type ScoreTrend = "improving" | "worsening" | "flat" | "initial" | null;

export function getScoreTrend(
  assessmentName: string,
  currentScore: number | string,
  previousScore: number | string | undefined
): ScoreTrend {
  // If no previous score, this is an initial assessment
  if (previousScore === undefined) return "initial";

  const direction = ASSESSMENT_SCORE_DIRECTION[assessmentName];
  if (!direction) return null;

  const current = Number(currentScore);
  const previous = Number(previousScore);
  if (Number.isNaN(current) || Number.isNaN(previous)) return null;

  // If scores are identical, trend is flat
  if (current === previous) return "flat";

  const wentUp = current > previous;
  const higherIsBetter = direction === "higher-is-better";
  return wentUp === higherIsBetter ? "improving" : "worsening";
}

export const getScoreDiff = (status: MemberFormStatus | undefined): string =>  {
  if (!status?.currentScore || !status.lastScore) {
    return "";
  }
  if (status.currentScore == status.lastScore) {
    return "";
  }

  const diff = status.currentScore - status.lastScore;
  return " (" + (diff > 0 ? "+" : "") + diff + ")";
}

export async function navigateToAssessment(
  formId: string,
  assessmentTypeId: string,
  patientId: string,
  setAllAnswersGiven: Dispatch<SetStateAction<boolean>>,
  navigate: ReturnType<typeof useNavigate>
): Promise<void> {

  if (formId === assessmentTypeId) {
    return;
  }

  LoadingHelper.showLoader("main");

  try {
    const assessments = await getAllMemberForms(patientId);

    const existingDraftAssessment = assessments?.data?.filter(
      (assessment) =>
        assessment.formId === formId &&
        assessment.status === ClinicalFormsConstants.draftStatus
    );

    if ((existingDraftAssessment ?? []).length > 0) {
      const draftAssessment = (existingDraftAssessment ?? [])[0];
      setAllAnswersGiven(false);
      navigate(`/patients/${patientId}/assessments/${draftAssessment.formId}/resume/${draftAssessment.id}`);
      return;
    }

    setAllAnswersGiven(false);
    navigate(`/patients/${patientId}/assessments/${formId}/start`);
  } catch (error) {
    console.error("Error in starting assessment", error);
  } finally {
    LoadingHelper.hideLoader("main");
  }
}
